import { cn } from '@/shared/lib/cn'
import { initials } from '@/shared/lib/format'
import type { UserSummary } from '@/shared/types/domain'
import { useT } from '@/shared/i18n'

export interface UserAvatarProps {
  user?: Pick<UserSummary, 'display_name'> | null
  size?: 'sm' | 'md'
  /** Muestra el nombre al lado del avatar. */
  withName?: boolean
  className?: string
}

/**
 * Sin responsable es un estado que hay que VER, no un hueco vacio.
 *
 * Siempre iniciales: el backend no guarda avatares (`UserRead` no tiene
 * `avatar_url`), así que el `<img>` que había acá nunca se iba a dibujar.
 */
export function UserAvatar({ user, size = 'sm', withName = false, className }: UserAvatarProps) {
  const t = useT()
  const dimension = size === 'sm' ? 'h-5 w-5 text-2xs' : 'h-7 w-7 text-xs'

  if (!user) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-text-3', className)}>
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex items-center justify-center rounded-full border border-dashed',
            'border-hairline text-text-3',
            dimension,
          )}
        >
          ?
        </span>
        {withName ? <span className="text-xs">{t('common.label.noOwner')}</span> : null}
      </span>
    )
  }

  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-1.5', className)}
      title={user.display_name}
    >
      <span
        aria-hidden="true"
        className={cn(
          'num inline-flex shrink-0 items-center justify-center rounded-full',
          'bg-surface-2 text-text-2',
          dimension,
        )}
      >
        {initials(user.display_name)}
      </span>
      {withName ? <span className="truncate text-xs text-text-1">{user.display_name}</span> : null}
    </span>
  )
}
